import { format, parseISO, addDays } from 'date-fns';
import { NonPaidPoint, NonPaidSourceForecast } from './types';
import { seededRandom, normalRandom } from './math';
import { computeAdaptiveJitter } from './adaptiveJitter';
import { findCampaignMatch } from '../../../lib/OriginResolver';
import { BoardEntity } from '../../../../../stores/boardStore';

export const computeUnknownSourceForecast = (
    finance: any,
    entities: BoardEntity[] | undefined,
    channels: any[] | undefined,
    forecastDays: number,
    sourceLabel: string,
    evolutionLabel: string
): { chart: NonPaidSourceForecast | null } => {
    const txs = finance?.transactions?.filter((tx: any) => tx.isActive !== false && tx.type === 'REVENUE') || [];
    if (txs.length < 7) return { chart: null };

    const unknownDaily: Record<string, number> = {};
    const allDates = new Set<string>();

    txs.forEach((tx: any) => {
        const dateKey = tx.date?.split('T')[0];
        if (!dateKey) return;

        const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};
        const isPaid = traffic.isPaid === true;

        if (!isPaid) return; // Non-paid goes to nonPaidForecastEngine

        const matchedEntity = findCampaignMatch(traffic, entities);

        let rootEntity = matchedEntity;
        if (matchedEntity) {
            let current = matchedEntity;
            let failsafe = 0;
            while (current && current.parentId && failsafe < 50) {
                failsafe++;
                const parent = entities?.find((e: any) => String(e.id) === String(current.parentId));
                if (parent) {
                    current = parent;
                } else {
                    break;
                }
            }
            rootEntity = current;
        }

        // Match the BoardChannel row that owns this entity. For pseudo-channel
        // rows, the row's enabled flag is what matters — its source entity
        // disabled-state is reflected on the row directly via the backend.
        const channel = matchedEntity
            ? channels?.find(
                (c: any) =>
                    String(c.channelId ?? c.id) === String(matchedEntity?.channelId) &&
                    (!c.isPseudoChannel || (rootEntity && c.sourceEntityId === rootEntity.id)),
            )
            : null;

        const isDisconnected = channel?.status === 'disconnected' || channel?.isEnabled === false;
        const isMatched = !!matchedEntity;
        const isSelected = !!matchedEntity?.isSelected;
        const isEnabled = matchedEntity?.isEnabled !== false;

        const isActive = isSelected && isEnabled && isMatched && !isDisconnected;

        // "unknown left revenue" that was filtered out
        if (!isActive) {
            allDates.add(dateKey);
            unknownDaily[dateKey] = (unknownDaily[dateKey] || 0) + (Number(tx.amount) || 0);
        }
    });

    const sortedDates = Array.from(allDates).sort();
    if (sortedDates.length < 7) return { chart: null };

    const fullSeries: (number | null)[] = sortedDates.map((d: string) => unknownDaily[d] ?? 0);
    const mathSeries: number[] = sortedDates.map((d: string) => unknownDaily[d] || 0);
    const validCount = mathSeries.filter((v: number) => v > 0).length;
    if (validCount === 0) return { chart: null };

    let lastSaleIdx = -1;
    for (let i = mathSeries.length - 1; i >= 0; i--) { if (mathSeries[i] > 0) { lastSaleIdx = i; break; } }
    const daysSinceLastSale = mathSeries.length - 1 - lastSaleIdx;

    const sourceMean = mathSeries.reduce((a, b) => a + b, 0) / mathSeries.length;

    // No baseline trend function, just a flat local mean 
    const level = sourceMean;

    // Detrended seasonality (without trend)
    const recentWindow = Math.min(28, sortedDates.length);
    const recentSlice = fullSeries.slice(-recentWindow);
    let localSum = 0, localCnt = 0;
    recentSlice.forEach((v) => { if (v !== null) { localSum += v; localCnt++; } });
    const locallyScopedMean = localCnt > 0 ? localSum / localCnt : 1;
    let smoothLvl = locallyScopedMean;
    const localLevels: number[] = [];
    recentSlice.forEach((v) => {
        if (v !== null) smoothLvl = 0.3 * v + 0.7 * smoothLvl;
        localLevels.push(smoothLvl);
    });

    const dowEffect = new Array(7).fill(0), dowCount = new Array(7).fill(0);
    recentSlice.forEach((val, i) => {
        if (val === null || localLevels[i] <= 0) return;
        const di = sortedDates.length - recentWindow + i;
        if (di < 0 || di >= sortedDates.length) return;
        const dow = new Date(sortedDates[di]).getDay();
        dowEffect[dow] += val / localLevels[i];
        dowCount[dow]++;
    });
    for (let d = 0; d < 7; d++) dowEffect[d] = dowCount[d] > 0 ? dowEffect[d] / dowCount[d] : 1;

    const npJitterProfile = computeAdaptiveJitter(
        mathSeries, mathSeries.filter(v => v > 0), sortedDates.length,
        daysSinceLastSale, undefined, true
    );

    const npCV = npJitterProfile.relativeCV;
    const npNoiseMult = npJitterProfile.noiseMultiplier;
    const npBandMult = npJitterProfile.bandWidthMultiplier;
    const sourceName = sourceLabel;

    const lastDateObj = new Date(sortedDates[sortedDates.length - 1]);
    const forecastResult: { baseline: number; noise: number; upper: number; lower: number }[] = [];

    for (let i = 0; i < forecastDays; i++) {
        const futureDate = addDays(lastDateObj, i + 1);
        const dow = futureDate.getDay();

        // No trend, just baseline level
        let base = level;

        const seasonConf = Math.exp(-i * 0.03);
        base *= 1 + (dowEffect[dow] - 1) * seasonConf;

        const safeBase = Math.max(0, base);
        const noiseSeed = seededRandom(`unk-${i}`);
        let noiseSample = 0;

        if (npJitterProfile.useImpulseNoise) {
            if (noiseSeed > (1 - npJitterProfile.impulseProbability)) {
                noiseSample = (noiseSeed - (1 - npJitterProfile.impulseProbability)) * safeBase * npCV * (10 / npJitterProfile.impulseProbability) * npNoiseMult;
            }
        } else {
            noiseSample = normalRandom(`unk-${i}`) * safeBase * npCV * npNoiseMult;
        }

        const margin = safeBase * npCV * npBandMult * Math.sqrt(1 + 0.12 * i);

        forecastResult.push({
            baseline: safeBase,
            noise: noiseSample,
            upper: Math.max(0, safeBase + 1.5 * margin),
            lower: Math.max(0, safeBase - 1.5 * margin)
        });
    }

    const displayDates = sortedDates.slice(-Math.min(30, sortedDates.length));
    const offset = sortedDates.length - displayDates.length;
    const chartData: NonPaidPoint[] = [];

    displayDates.forEach((date, di) => {
        const idx = offset + di;
        const val = fullSeries[idx];
        const isLast = di === displayDates.length - 1;
        chartData.push({
            label: format(parseISO(date), 'MMM d'),
            date: date,
            actual: val,
            baseline: isLast ? val : null,
            stochasticNoise: 0,
            upper: isLast ? val : null,
            lower: isLast ? val : null
        });
    });

    for (let i = 0; i < forecastDays; i++) {
        const futureDate = addDays(lastDateObj, i + 1);
        chartData.push({
            label: format(futureDate, 'MMM d'),
            date: futureDate.toISOString().split('T')[0],
            actual: null,
            baseline: forecastResult[i].baseline,
            stochasticNoise: forecastResult[i].noise,
            upper: forecastResult[i].upper,
            lower: forecastResult[i].lower
        });
    }

    return {
        chart: {
            source: sourceName,
            color: '#6b7280', // Gray color for unknown
            evolutionLabel: evolutionLabel,
            data: chartData,
            backtestScore: null,
            jitterProfile: npJitterProfile
        }
    };
};
